"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { LANGUAGE_LABELS } from "@/lib/constants";

interface Topic {
  id: string;
  name: string;
}

interface MediumLabel {
  id: string;
  key: string;
  name: string;
  color: string | null;
  icon: string | null;
}

interface ShowFiltersProps {
  topics: Topic[];
  mediumLabels: MediumLabel[];
  basePath?: string;
}

export function ShowFilters({
  topics,
  mediumLabels,
  basePath = "/shows-entdecken",
}: ShowFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [selectedTopics, setSelectedTopics] = useState<string[]>(
    searchParams.getAll("topic")
  );
  const [selectedMediums, setSelectedMediums] = useState<string[]>(
    searchParams.getAll("medium")
  );
  const [sprache, setSprache] = useState(searchParams.get("sprache") || "");
  const [suchtGaeste, setSuchtGaeste] = useState(searchParams.get("suchtGaeste") === "true");
  const [callInShow, setCallInShow] = useState(searchParams.get("callInShow") === "true");

  function applyFilters(overrides?: {
    q?: string;
    topics?: string[];
    mediums?: string[];
    sprache?: string;
    suchtGaeste?: boolean;
    callInShow?: boolean;
  }) {
    const params = new URLSearchParams();
    const fQ = overrides?.q ?? q;
    const fTopics = overrides?.topics ?? selectedTopics;
    const fMediums = overrides?.mediums ?? selectedMediums;
    const fSprache = overrides?.sprache ?? sprache;
    const fSuchtGaeste = overrides?.suchtGaeste ?? suchtGaeste;
    const fCallInShow = overrides?.callInShow ?? callInShow;

    if (fQ) params.set("q", fQ);
    fTopics.forEach((t) => params.append("topic", t));
    fMediums.forEach((medium) => params.append("medium", medium));
    if (fSprache) params.set("sprache", fSprache);
    if (fSuchtGaeste) params.set("suchtGaeste", "true");
    if (fCallInShow) params.set("callInShow", "true");

    router.push(`${basePath}?${params.toString()}`);
  }

  function toggleTopic(id: string) {
    const next = selectedTopics.includes(id)
      ? selectedTopics.filter((t) => t !== id)
      : [...selectedTopics, id];
    setSelectedTopics(next);
    applyFilters({ topics: next });
  }

  function toggleMedium(key: string) {
    const next = selectedMediums.includes(key)
      ? selectedMediums.filter((medium) => medium !== key)
      : [...selectedMediums, key];
    setSelectedMediums(next);
    applyFilters({ mediums: next });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row">
        <Input
          placeholder="Suche nach Titel oder Beschreibung..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
          className="flex-1"
        />
        <select
          value={sprache}
          onChange={(e) => {
            setSprache(e.target.value);
            applyFilters({ sprache: e.target.value });
          }}
          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
        >
          <option value="">Alle Sprachen</option>
          {Object.entries(LANGUAGE_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Switch
            checked={suchtGaeste}
            onCheckedChange={(checked) => {
              setSuchtGaeste(checked);
              applyFilters({ suchtGaeste: checked });
            }}
          />
          <Label className="text-sm whitespace-nowrap">Sucht Gäste</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={callInShow}
            onCheckedChange={(checked) => {
              setCallInShow(checked);
              applyFilters({ callInShow: checked });
            }}
          />
          <Label className="text-sm whitespace-nowrap">Call-in Show</Label>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {topics.map((topic) => (
          <Badge
            key={topic.id}
            variant={selectedTopics.includes(topic.id) ? "default" : "outline"}
            className="cursor-pointer select-none text-xs"
            onClick={() => toggleTopic(topic.id)}
          >
            {topic.name}
          </Badge>
        ))}
      </div>

      {mediumLabels.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Medien</p>
          <div className="flex flex-wrap gap-1.5">
            {mediumLabels.map((mediumLabel) => {
              const isSelected = selectedMediums.includes(mediumLabel.key);

              return (
                <Badge
                  key={mediumLabel.id}
                  variant={isSelected ? "default" : "outline"}
                  className="cursor-pointer select-none text-xs"
                  style={{
                    backgroundColor:
                      isSelected && mediumLabel.color
                        ? `${mediumLabel.color}22`
                        : undefined,
                    borderColor: mediumLabel.color ?? undefined,
                    color: mediumLabel.color ?? undefined,
                  }}
                  onClick={() => toggleMedium(mediumLabel.key)}
                >
                  {mediumLabel.icon ? <span aria-hidden="true">{mediumLabel.icon} </span> : null}
                  {mediumLabel.name}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
